import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Calendar, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";

export default function Contact() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      q: "What is ESHA?",
      a: "ESHA (Eternal Soul Healing & Awakening) is a comprehensive transformational ecosystem integrating emotional intelligence, somatic nervous-system regulation, energy awareness, and practical life-design. We help individuals move from reactive, survival-driven patterns into conscious, aligned living."
    },
    {
      q: "Who is ESHA for?",
      a: "ESHA is designed specifically for seekers, parents, couples, professionals, and leaders ready to take absolute personal responsibility for their growth. If you are experiencing chronic emotional exhaustion, relationship friction, self-doubt, or feel stuck despite outer achievement, ESHA offers a grounded, practical pathway."
    },
    {
      q: "Where should I start?",
      a: "We highly recommend starting with the Emotional Freedom Masterclass, which is our most accessible gateway experience. Alternatively, if you seek direct customized guidance, you can apply to book a Private Clarity Call with our senior advisors."
    },
    {
      q: "What is the Emotional Freedom Blueprint?",
      a: "The Emotional Freedom Blueprint is ESHA's foundational pathway. It focuses strictly on somatic nervous-system settling, mapping recurring emotional triggers, identifying automatic survival-driven behaviors, and cultivating an unshakeable bedrock of personal self-worth."
    },
    {
      q: "What are the Core Mastery Programs?",
      a: "Our Mastery programs consist of four specialized tracks aimed at deep life refinement: Self Mastery (self-worth, confidence, boundaries), Relationship Mastery (conscious communication, relationship trigger safe-zones), Emotional Mastery (advanced emotional alchemy and stability), and Abundance Mastery (dissolving ancestral money shame and practicing conscious receiving)."
    },
    {
      q: "What is ESHA Life Mastery?",
      a: "ESHA Life Mastery is our integrated ultimate experience. It serves as an ultimate synthesis, guiding advanced students and leaders to consciously design their careers, relationship environments, and service projects around deep purpose, energetic alignment, and legacy leadership."
    },
    {
      q: "Is ESHA therapy or psychiatric care?",
      a: "No. ESHA provides coaching, educational instruction, somatic practices, and personal development guidance. It is not clinical therapy, psychiatry, or medical mental-health treatment, and does not substitute for licensed clinical care. We encourage anyone experiencing clinical mental difficulties to seek support from licensed professionals."
    },
    {
      q: "Where can I see upcoming program dates?",
      a: "Our central authority for all published programs, workshops, and availability is our official ESHA Offerings Hub, accessible right here at learn.eshawitheswari.com/offerings."
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#050505] text-[#F6F4EE] relative" data-testid="contact-page">
      <div className="absolute inset-0 glow-radial opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] px-3 py-1.5 border border-[#00D2FF]/20 rounded-full bg-[#111111]/85 font-bold">
            Get In Touch
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F6F4EE] leading-tight mt-6 mb-6">
            Contact & <span className="text-blue-shimmer italic">Inquiry</span>
          </h1>
          <p className="font-sans text-base text-[#AAA29E] leading-relaxed">
            Have questions about enrollment, custom corporate advisory, or need guidance on select pathways? Reach out to our ESHA admissions and service team.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="mb-28">
          {/* Contact details */}
          <div className="max-w-3xl mx-auto flex flex-col justify-between">
            <div className="space-y-8">
              <h2 className="font-serif text-2xl sm:text-3xl text-[#F6F4EE] font-light">
                Direct Inquiry Channels
              </h2>
              <p className="font-sans text-sm text-[#AAA29E] leading-relaxed">
                For immediate service, you can also leverage our primary digital portals. If you are seeking immediate coaching selection, applying for a Clarity Call is highly recommended.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4 items-start bg-[#0F0F0F] border border-[#FF8C00]/15 p-5 rounded-xl shadow-md">
                  <Mail className="h-5 w-5 text-[#00D2FF] mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-serif text-[#F6F4EE] text-sm font-semibold">Email Correspondence</h4>
                    <p className="text-xs text-[#AAA29E] mt-1">support@eshawitheswari.com</p>
                    <p className="text-[10px] text-[#AAA29E]/60 mt-0.5">Response within 24-48 business hours</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start bg-[#0F0F0F] border border-[#FF8C00]/15 p-5 rounded-xl shadow-md">
                  <Calendar className="h-5 w-5 text-[#C87D53] mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-serif text-[#F6F4EE] text-sm font-semibold">ESHA Offerings Hub</h4>
                    <p className="text-xs text-[#AAA29E] mt-1">learn.eshawitheswari.com/offerings</p>
                    <Link
                      to="/offerings"
                      className="text-[10px] uppercase text-[#00D2FF] tracking-widest font-mono font-bold mt-2 block"
                    >
                      Explore Storefront ↗
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border border-[#FF8C00]/15 rounded-2xl bg-[#0F0F0F]/50 mt-12 shadow-md">
              <h4 className="font-serif text-sm font-bold text-[#F6F4EE] mb-2">Admissions Notice</h4>
              <p className="text-xs text-[#AAA29E] leading-relaxed">
                To keep our transformational environments high-integrity and dedicated, ESHA admissions are based strictly on student alignment, focus, and personal readiness.
              </p>
            </div>
          </div>

        </div>

        {/* SECTION FAQ */}
        <div className="border-t border-[#FF8C00]/15 pt-20" data-testid="faq-section">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <HelpCircle className="h-8 w-8 text-[#00D2FF] mx-auto mb-4" />
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE]">
              Frequently Asked Questions
            </h2>
            <p className="font-sans text-sm text-[#AAA29E] mt-4">
              Find instant clarification regarding ESHA teachings, program sequences, and advisory paths.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = expandedFaq === idx;
              return (
                <div
                  key={idx}
                  className="bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl overflow-hidden transition-all duration-300 shadow-md"
                  data-testid={`faq-item-${idx}`}
                >
                  <button
                    onClick={() => setExpandedFaq(isOpen ? null : idx)}
                    className="w-full p-6 text-left flex justify-between items-center gap-4 text-[#F6F4EE] hover:text-[#00D2FF] transition-colors"
                    aria-expanded={isOpen}
                    data-testid={`faq-trigger-${idx}`}
                  >
                    <span className="font-serif text-base sm:text-lg font-medium">{faq.q}</span>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-[#00D2FF]" /> : <ChevronDown className="h-4 w-4 text-[#AAA29E]" />}
                  </button>

                  {isOpen && (
                    <div className="p-6 pt-0 border-t border-[#FF8C00]/10" data-testid={`faq-answer-${idx}`}>
                      <p className="font-sans text-xs sm:text-sm text-[#AAA29E] leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
